import _ from 'lodash';
import React from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { SectionContainer, SectionTitle } from '../common/styles';
import Actions from '../common/actions';
import Filters from '../common/filters';
import Loader from '../common/loader';
import useFollowersStore from './followers-components/state-hook';
import Figures from './followers-components/figures';
import { getFollowers } from '../../../services/analytics/audience-service';
import ChartArea from './followers-components/chart-area';

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 340px;
  justify-content: space-between;
  align-items: center;
  margin-top: 56px;

  & > *:first-child {
    order: -1;
    height: 100%;
    /* The whole desktop view only supports two resolutions: XLarge(1024px~1365px) and XXLarge(1366px+) */
    flex-basis: ${props => (props.theme.isXXLarge ? '33%' : '24%')};
  }

  &:after {
    order: 0;
    height: 100%;
    content: "";
    width: 22px;
    display: block;
  }

  & > *:last-child {
    order: 1;
    height: 100%;
    /* The whole desktop view only supports two resolutions: XLarge(1024px~1365px) and XXLarge(1366px+) */
    flex-basis: ${props => (props.theme.isXXLarge ? '67%' : '76%')};
  }
`;

export default function Followers({
  id,
  cachedAnalyticsDataUrls,
  contributors,
  actualDaysAgo,
}) {
  const cachedFollowersDataUrl = cachedAnalyticsDataUrls.followersDataUrl;
  const setContributors = useFollowersStore(state => state.setContributors);
  const setRawFollowers = useFollowersStore(state => state.setRawFollowers);
  const setDaysAgo = useFollowersStore(state => state.setDaysAgo);
  const set = useFollowersStore(state => state.set);
  const storeContribs = useFollowersStore(state => state.contributors);
  const daysAgo = useFollowersStore(state => state.daysAgo);
  const generatedAt = useFollowersStore(state => state.generatedAt);
  const requestData = useFollowersStore(state => state.requestData);
  const storeLoading = useFollowersStore(state => state.loading);
  const [loading, setLoading] = React.useState(false);
  const [inited, setInited] = React.useState(false);
  const [token, setToken] = React.useState(null);

  const fetchFollowers = React.useCallback(fromCache => {
    if (!_.get(contributors, 'length')) {
      return;
    }

    setLoading(true);
    const [task, source] = getFollowers(
      id,
      contributors.map(c => c.id),
      actualDaysAgo,
      cachedFollowersDataUrl,
      fromCache,
    );
    setToken(source);
    task
      .then(response => {
        const payload = _.get(response, 'data', []);
        if (payload.length) {
          setInited(true);
          setRawFollowers(payload);
          set(state => {
            state.generatedAt = moment();
          });
        }
      })
      .finally(() => {
        set(state => {
          state.requestData = false;
        });
        setLoading(false);
        setToken(null);
      });
  }, [contributors, id, actualDaysAgo, set, setRawFollowers, cachedFollowersDataUrl]);

  React.useEffect(() => {
    if (!inited) {
      setContributors(_.map(contributors, c => ({ ...c, selected: true })));
      fetchFollowers(true);
    }
  }, [inited, contributors, fetchFollowers, setContributors]);

  React.useEffect(() => {
    requestData && fetchFollowers(false);
  }, [requestData, fetchFollowers]);

  React.useEffect(() => {
    let tid;
    if (loading) {
      tid = setTimeout(() => set(state => {
        state.loading = true;
      }), 200);
    } else {
      set(state => {
        state.loading = false;
      });
    }
    return () => clearTimeout(tid);
  }, [loading, set]);

  const onCancel = () => token && token.cancel();

  const filtersProps = {
    daysAgo,
    contributors: storeContribs,
    onContributorsChange: setContributors,
    onDateChange: setDaysAgo,
  };

  const actionsProps = {
    generatedAt,
    onRefreshClick: () => set(state => {
      state.requestData = true;
    }),
  };

  return (
    <SectionContainer>
      {!!storeLoading && <Loader cancellable={inited} onCancel={onCancel} />}
      <Filters {...filtersProps} />
      <Actions {...actionsProps} />
      <SectionTitle>followers</SectionTitle>
      <Wrapper>
        <Figures />
        <ChartArea />
      </Wrapper>
    </SectionContainer>
  );
}

Followers.propTypes = {
  id: PropTypes.number.isRequired,
  cachedAnalyticsDataUrls: PropTypes.shape({
    followersDataUrl: PropTypes.string,
  }).isRequired,
  contributors: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    profilePictureUrl: PropTypes.string,
    createdAt: PropTypes.string,
  })).isRequired,
  actualDaysAgo: PropTypes.number.isRequired,
};
